<template>
  <section class="flex w-full align-middle justify-center sm:w-auto sm:py-40">
    <div class="flex flex-col w-full sm:w-auto px-8 sm:px-0">
      <Card class="!bg-transparent sm:!bg-white box-shadow-none sm:box-shadow-sm sm:w-448 max-w-screen mt-20 sm:mt-0 p-24">
        <form id="add-employer-form" @submit.prevent="submit()">
          <h1 class="text-h1 mb-24">Add your employer</h1>
          <p v-if="employerExperimentIsOn" class="text-body text-grey-body mt-16 mb-24">
            We'll check to see if your workplace offers Sittercity as a benefit.
          </p>
          <fieldset>
            <SearchComboBox
              id="companySearchBox"
              ref="companySearchBox"
              allowEmptyResults
              :results="clientSearchResults"
              type="text"
              :labelName="`Your employer's name ${employerExperimentIsOn ? '(optional)' : ''}`"
              class="mb-24"
              :helperText="`${employerExperimentIsOn ? 'Select your employer from the dropdown' : 'Optional'}`"
              :status="validationError ? HelperStatus.Error : HelperStatus.Neutral"
              :errorText="validationError"
              @selected="queryFragment"
            />
          </fieldset>
          <Button class="w-full" variant="primary" value="Continue" size="medium" type="submit" />
          <Button
            v-if="employerExperimentIsOn"
            class="w-full mt-16"
            variant="text"
            value="Skip for now"
            size="medium"
            type="button"
            @click="skip"
          />
        </form>
      </Card>
    </div>
  </section>
</template>

<script lang="ts">
import SearchComboBox from '@/components/search_combo_box/SearchComboBox.vue';
import Button from '@/components/button/Button.vue';
import Card from '@/components/card/Card.vue';

import { sendBrazeEvent } from '../../../../lib/braze';
import { BASIC_REGISTRATION_ADD_YOUR_EMPLOYER_LOADED } from '../../../../lib/constants/braze_custom_events';
import { sendSegmentEvent } from '@/lib/segment';
import { isOn } from '@/lib/growthbook';

import HelperStatus from '@/helpers/status';
import { CLIENT_SEARCH_QUERY, CORPORATE_CLIENT_QUERY } from '../../../../graphql/bh_benefits/queries';

export default {
  name: 'AddYourEmployer',
  components: {
    SearchComboBox,
    Button,
    Card,
  },
  data(): any {
    return {
      employerNameInput: '',
      selectedCompanySlug: '',
      selectedCompanyName: '',
      selectedBhClientId: '',
      clientSearchResults: [],
      validationError: '',
      selectionAttempted: false,
      HelperStatus,
    };
  },
  computed: {
    employerExperimentIsOn(): boolean {
      return isOn('sc-onboarding-employer-pg');
    },
  },
  mounted() {
    sendBrazeEvent(BASIC_REGISTRATION_ADD_YOUR_EMPLOYER_LOADED);
  },
  methods: {
    invalidEmployerEntered() {
      sendSegmentEvent('Invalid Employer Entered', {
        employerEntered: this.employerNameInput,
      });
    },
    validateEmployerEntered(employer: string): boolean {
      return employer?.trim().length > 0;
    },
    async queryGetClientSlug(bhClientId: string) {
      await this.$apollo
        .query({
          query: CORPORATE_CLIENT_QUERY,
          variables: {
            bhClientId: Number(bhClientId),
          },
        })
        .then(({ data }) => {
          const { slug, name } = data.corporateClient;
          this.selectedCompanySlug = slug;
          this.selectedCompanyName = name;
          this.selectedBhClientId = bhClientId;
        })
        .catch((error) => {
          this.selectedCompanySlug = '';
          this.selectedCompanyName = '';
          this.selectedBhClientId = '';
          console.warn('GraphQL query error:', error);
        });
    },
    async queryFragment({ value, display }) {
      this.employerNameInput = display;
      if (value) {
        // user selected client from dropdown
        this.selectionAttempted = true;
        await this.queryGetClientSlug(value);

        // clear validation error when valid selection is made
        this.validationError = '';
      } else {
        // user typing in fragment
        this.selectionAttempted = false;
        await this.$apollo
          .query({
            query: CLIENT_SEARCH_QUERY,
            variables: {
              fragment: display,
            },
          })
          .then(({ data }) => {
            const corporateClientsForComboBox = data.clientSearch.map(({ clientId, clientName }) => {
              return { display: clientName, value: `${clientId}` };
            });
            this.selectedCompanySlug = '';
            this.selectedCompanyName = '';
            this.selectedBhClientId = '';
            this.clientSearchResults = corporateClientsForComboBox;
            this.validationError = '';
          })
          .catch((error) => {
            console.error('GraphQL query error:', error);
          });
      }
    },
    skip() {
      this.$router.push({ name: 'registration-care-type' });
    },
    submit() {
      this.validationError = '';

      if (this.employerExperimentIsOn) {
        if (!this.selectedCompanySlug) {
          if (this.selectionAttempted) {
            this.validationError = "We couldn't confirm a benefit match for this employer. You can skip this step to continue.";
            this.invalidEmployerEntered();
          } else {
            this.validationError = "Please select your employer from the list or skip this step if it's not listed.";
            if (this.validateEmployerEntered(this.employerNameInput)) {
              this.invalidEmployerEntered();
            }
          }
          return;
        }

        this.$router.push({ name: 'registration-request-upgrade', query: { slug: this.selectedCompanySlug } });
      } else if (!this.selectedCompanySlug) {
        // Track invalid employer if user typed something
        if (this.validateEmployerEntered(this.employerNameInput)) {
          this.invalidEmployerEntered();
        }
        // No valid employer selected - allow skip to care type
        this.$router.push({ name: 'registration-care-type' });
      } else {
        // Valid employer selected - proceed to upgrade request
        this.$router.push({ name: 'registration-request-upgrade', query: { slug: this.selectedCompanySlug } });
      }
    },
  },
};
</script>
